<template>
    <modal id="custom-insoles-modal" size="extra-large" v-model="showModal" :loading="loading" :preloader="preloader"
        title="Custom Insole Order Form" :invoice="invoice" :item="item" @submit="submitData">
        <form ref="form" :data-url='selectedUrl ? selectedUrl : CUSTOM_INSOLES_CUSTOMIZATION' @submit.prevent="submitData">
            <div class="container">
                <!-- ===== Auto-populated header (left) + Order QR (right), top-aligned ===== -->
                <div class="row mb-2">
                    <div class="col-md-10">
                        <div class="row mb-2">
                            <div class="col-md-4"><span class="text-muted">Age:</span> {{ patientAge }}</div>
                            <div class="col-md-4"><span class="text-muted">Weight:</span> {{ patientWeight }}</div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-md-4"><span class="text-muted">Branch:</span> {{ branchName }}</div>
                            <div class="col-md-4"><span class="text-muted">Practitioner:</span> {{ practitionerName }}</div>
                            <div class="col-md-4"><span class="text-muted">Order:</span> {{ invoice.invoice_number }}</div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-md-4"><span class="text-muted">Order Date:</span> {{ orderDate }}</div>
                            <div class="col-md-4"><span class="text-muted">Target Date:</span> {{ targetDateDisplay }}</div>
                            <div class="col-md-4"><span class="text-muted">Revised Target:</span> {{ revisedTargetDisplay }}</div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-md-8">
                                <span class="text-muted">Workflow:</span>
                                <template v-if="workflowSteps.length">
                                    <span v-for="(s, i) in workflowSteps" :key="i">{{ getStepName(s) }}<span v-if="i < workflowSteps.length - 1"> &rarr; </span></span>
                                </template>
                                <span v-else>-</span>
                            </div>
                            <div class="col-md-4"><span class="text-muted">Mode:</span> {{ modeLabel }}</div>
                        </div>
                    </div>
                    <div class="col-md-2 text-right">
                        <img v-if="orderQrImage" :src="orderQrImage" alt="Order QR" title="Order QR (Order ID, patient, items)" style="width:110px;height:110px;" />
                    </div>
                </div>
                <hr class="mt-2 mb-3">
                <div class="row mb-3">
                    <div class="col-md-6"><span class="text-muted">Qty:</span> {{ qtyDisplay }}</div>
                </div>

                <!-- Composite -->
                <div class="row mb-3">
                    <div class="col-md-2 d-flex align-items-center">
                        <label for="Composite">Composite:</label>
                    </div>
                    <div class="col-md-10">
                        <select class="col-md-11" v-model="formData.composite_id">
                            <option value="">Select Composite</option>
                            <option v-for="option in compositeProducts" :key="option.id" :value="option.id">
                                {{ option.value }}
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Custom Module (revealed only when Composite = Custom) -->
                <div class="row mb-3" v-if="isCustomComposite">
                    <div class="col-md-12">
                        <label for="CustomModule">Custom Module <span class="text-danger">*</span></label>
                        <textarea class="form-control" name="custom_modules" v-model="formData.custom_modules"
                            id="CustomModule" rows="3" maxlength="100"
                            placeholder="Describe the custom module (max 100 characters)..."></textarea>
                        <small class="text-muted">{{ (formData.custom_modules || '').length }}/100</small>
                    </div>
                </div>

                <!-- Insole Width -->
                <div class="row mb-3">
                    <div class="col-md-12">
                        <app-form-group v-model="formData.insole_width" type="radio" class="mb-2"
                            :label="$t('Insole Width')" :list="insoleWidths"
                            :error-message="$errorMessage(errors, 'insole_width')" />
                    </div>
                </div>

                <!-- Insole template dimensions (always shown) -->
                <div class="row mb-3">
                    <div class="col-md-6 mb-3">
                        <label for="Insole template Length">Insole template Length</label>
                        <select id="templateLength"
                            class="ci_templateLength form-control"
                            name="template_length" v-model="formData.template_length" style="width: 100%;">
                            <option value="">Select Length</option>
                            <option v-for="row in templateLengthOrWidthOptions" :key="row.id" :value="row.id">
                                {{ row.value }}
                            </option>
                        </select>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label for="Insole template Width">Insole template Width</label>
                        <select id="templateWidth"
                            class="ci_templateWidth form-control"
                            name="templateWidth" v-model="formData.template_width" style="width: 100%;">
                            <option value="">Select Width</option>
                            <option v-for="row in templateLengthOrWidthOptions" :key="row.id" :value="row.id">
                                {{ row.value }}
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Are insoles being made for specific shoes? -->
                <div class="row mb-2">
                    <div class="col-md-12">
                        <app-form-group v-model="formData.made_for_shoes" type="radio" class="mb-2"
                            :label="$t('Are the insoles being made for specific shoes?')" :list="yesNoOptions"
                            :error-message="$errorMessage(errors, 'made_for_shoes')" />
                    </div>
                </div>

                <!-- Shoe details (only when made for specific shoes) -->
                <div class="row mb-3" v-if="formData.made_for_shoes == 1">
                    <div class="col-md-4 mb-3">
                        <label for="Shoe type">Shoe type</label>
                        <select id="shoeType"
                            class="ci_shoeType form-control"
                            name="shoe_type" v-model="formData.shoe_type" style="width: 100%;">
                            <option value="">Select Shoe type</option>
                            <option v-for="row in shoeTypeOptions" :key="row.id" :value="row.id">
                                {{ row.value }}
                            </option>
                        </select>
                    </div>
                    <div class="col-md-4 mb-3">
                        <label for="Shoe heel height">Shoe heel height</label>
                        <template>
                            <app-input type="text" :placeholder="$placeholder('Shoe heel height')" :inputclearable="false"
                                v-model="formData.shoe_heel" />
                        </template>
                    </div>
                    <div class="col-md-4 mb-3">
                        <label for="Shoe brand">Shoe brand</label>
                        <input type="text" class="form-control" maxlength="15"
                            :placeholder="$placeholder('Shoe brand')" v-model="formData.shoe_brand" />
                    </div>
                    <div class="col-md-4 mb-3">
                        <label for="Shoe Size">Shoe Size</label>
                        <select id="shoeSize"
                            class="ci_shoeSize form-control"
                            name="shoe_size" v-model="formData.shoe_size" style="width: 100%;">
                            <option value="">Select Shoe Size</option>
                            <option v-for="row in shoeSizeOptions" :key="row.id" :value="row.id">
                                {{ row.value }}
                            </option>
                        </select>
                    </div>
                    <div class="col-md-8 mb-3">
                        <app-form-group v-model="formData.shoe_volume" type="radio" class="mb-2"
                            :label="$t('Shoe volume')" :list="shoeVolumes"
                            :error-message="$errorMessage(errors, 'shoe_volume')" />
                    </div>
                </div>

                <!-- Linked Orders -->
                <div class="row mb-3">
                    <div class="col-md-12">
                        <app-form-group v-model="formData.linked_orders" type="radio" class="mb-2"
                            :label="$t('Linked Orders')" :list="linkedOrders" :required="true"
                            :error-message="$errorMessage(errors, 'linked_orders')" />
                    </div>
                </div>

                <!-- Technique / Medial wall / Back cover -->
                <div class="row mb-3">
                    <div class="col-md-4 mb-3">
                        <label for="Technique">Moulding Technique <span class="text-danger">*</span></label>
                        <select id="technique" class="ci_mould form-control"
                            name="mould" v-model="formData.mould" style="width: 100%;">
                            <option value="">Select Moulding Technique</option>
                            <option v-for="row in techniqueOptions" :key="row.id" :value="row.id">
                                {{ row.value }}
                            </option>
                        </select>
                    </div>
                    <div class="col-md-4 mb-3">
                        <label for="Medial wall">Insole Flange <span class="text-danger">*</span></label>
                        <select class="form-control" v-model="formData.medial_wall">
                            <option value="">Select Insole Flange</option>
                            <option v-for="row in medialWallOptions" :key="row.id" :value="row.id">
                                {{ row.value }}
                            </option>
                        </select>
                    </div>
                    <div class="col-md-4 mb-3">
                        <label for="Back cover">Back cover <span class="text-danger">*</span></label>
                        <select class="form-control" v-model="formData.back_cover">
                            <option value="">Select Back cover</option>
                            <option v-for="row in backCoverOptions" :key="row.id" :value="row.id">
                                {{ row.value }}
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Sidas last size (only for Sidas moulding techniques) -->
                <div class="row mb-3" v-if="isSidasTechnique">
                    <div class="col-md-4 mb-3">
                        <label for="SidasLastSize">Sidas last size <span class="text-danger">*</span></label>
                        <select id="SidasLastSize" class="form-control" v-model="formData.sidas_last_size">
                            <option value="">Select Sidas last size</option>
                            <option v-for="row in sidasLastSizeOptions" :key="row.id" :value="row.id">
                                {{ row.value }}
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Delivery -->
                <div class="row mb-3">
                    <div class="col-md-12 mb-3">
                        <label for="Delivery">Delivery</label>
                        <select class="form-control" v-model="formData.delivery">
                            <option value="">Select Delivery</option>
                            <option value="in_trial">In trial</option>
                            <option value="finished">Finished</option>
                        </select>
                    </div>
                </div>

                <!-- Layering -->
                <div class="row">
                    <div class="col-md-12 row">
                        <div class="col-md-4">
                            <div class="font-weight-bold mb-4">Layering</div>
                        </div>
                        <div class="col-md-4">
                            <div class="font-weight-bold mb-4">Left</div>
                        </div>
                        <div class="col-md-4">
                            <div class="font-weight-bold mb-4">Right</div>
                        </div>
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-12 row mb-3 d-flex align-items-center">
                        <div class="col-md-4">
                            <label for="Addition1">Addition 1:</label>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.layering.layering_left_id_1"
                                @change="resetQuantity('layering_left')">
                                <option value="">Select Layering Left</option>
                                <option v-for="option in layeringProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityLayeringLeft">{{ exceedsQuantityLayeringLeft
                            }}</small>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.layering.layering_right_id_1"
                                @change="resetQuantity('layering_right')">
                                <option value="">Select Layering Right</option>
                                <option v-for="option in layeringProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityLayeringRight">{{ exceedsQuantityLayeringRight
                            }}</small>
                        </div>
                    </div>
                    <div class="col-md-12 row mb-3 d-flex align-items-center">
                        <div class="col-md-4">
                            <label for="Addition2">Addition 2:</label>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.layering.layering_left_id_2"
                                @change="resetQuantity('layering_left')">
                                <option value="">Select Layering Left</option>
                                <option v-for="option in layeringProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityLayeringLeft">{{ exceedsQuantityLayeringLeft
                            }}</small>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.layering.layering_right_id_2"
                                @change="resetQuantity('layering_right')">
                                <option value="">Select Layering Right</option>
                                <option v-for="option in layeringProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityLayeringRight">{{ exceedsQuantityLayeringRight
                            }}</small>
                        </div>
                    </div>
                    <div class="col-md-12 row mb-3 d-flex align-items-center">
                        <div class="col-md-4">
                            <label for="Addition3">Addition 3:</label>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.layering.layering_left_id_3"
                                @change="resetQuantity('layering_left')">
                                <option value="">Select Layering Left</option>
                                <option v-for="option in layeringProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityLayeringLeft">{{ exceedsQuantityLayeringLeft
                            }}</small>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.layering.layering_right_id_3"
                                @change="resetQuantity('layering_right')">
                                <option value="">Select Layering Right</option>
                                <option v-for="option in layeringProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityLayeringRight">{{ exceedsQuantityLayeringRight
                            }}</small>
                        </div>
                    </div>
                    <div class="col-md-12 row mb-3 d-flex align-items-center">
                        <div class="col-md-4">
                            <label for="Addition4">Addition 4:</label>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.layering.layering_left_id_4"
                                @change="resetQuantity('layering_left')">
                                <option value="">Select Layering Left</option>
                                <option v-for="option in layeringProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityLayeringLeft">{{ exceedsQuantityLayeringLeft
                            }}</small>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.layering.layering_right_id_4"
                                @change="resetQuantity('layering_right')">
                                <option value="">Select Layering Right</option>
                                <option v-for="option in layeringProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityLayeringRight">{{ exceedsQuantityLayeringRight
                            }}</small>
                        </div>
                    </div>
                    <div class="col-md-12 row mb-3 d-flex align-items-center">
                        <div class="col-md-4">
                            <label for="Addition5">Addition 5:</label>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.layering.layering_left_id_5"
                                @change="resetQuantity('layering_left')">
                                <option value="">Select Layering Left</option>
                                <option v-for="option in layeringProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityLayeringLeft">{{ exceedsQuantityLayeringLeft
                            }}</small>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.layering.layering_right_id_5"
                                @change="resetQuantity('layering_right')">
                                <option value="">Select Layering Right</option>
                                <option v-for="option in layeringProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityLayeringRight">{{ exceedsQuantityLayeringRight
                            }}</small>
                        </div>
                        <div class="col-md-4">
                        </div>
                        <div class="col-md-4">
                        </div>
                        <div class="col-md-4">
                            <label for="sameAsLeft" class="mr-1">Same as Left:</label>
                            <input type="checkbox" v-model="sameAsLeft.layering" @change="setRightSameAsLeft('layering', 1)">
                        </div>
                    </div>
                </div>

                <!-- Reinforcements -->
                <div class="row">
                    <div class="col-md-12 row">
                        <div class="col-md-4">
                            <div class="font-weight-bold mb-4">Reinforcements</div>
                        </div>
                        <div class="col-md-4">
                            <div class="font-weight-bold mb-4">Left</div>
                        </div>
                        <div class="col-md-4">
                            <div class="font-weight-bold mb-4">Right</div>
                        </div>
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-12 row mb-3 d-flex align-items-center">
                        <div class="col-md-4">
                            <label for="Addition1">Addition 1:</label>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.reinforcements.reinforcements_left_id_1"
                                @change="resetQuantity('reinforcements_left', 'reinforcements', 1)">
                                <option value="">Select Reinforcements Left</option>
                                <option v-for="option in reinforcementsProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityReinforcementsLeft">{{
                                exceedsQuantityReinforcementsLeft
                            }}</small>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.reinforcements.reinforcements_right_id_1"
                                @change="resetQuantity('reinforcements_right', 'reinforcements', 1)">
                                <option value="">Select Reinforcements Right</option>
                                <option v-for="option in reinforcementsProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityReinforcementsRight">{{
                                exceedsQuantityReinforcementsRight
                            }}</small>
                        </div>
                    </div>
                    <div class="col-md-12 row mb-3 d-flex align-items-center">
                        <div class="col-md-4">
                            <label for="Addition2">Addition 2:</label>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.reinforcements.reinforcements_left_id_2"
                                @change="resetQuantity('reinforcements_left', 'reinforcements', 1)">
                                <option value="">Select Reinforcements Left</option>
                                <option v-for="option in reinforcementsProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityReinforcementsLeft">{{
                                exceedsQuantityReinforcementsLeft
                            }}</small>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.reinforcements.reinforcements_right_id_2"
                                @change="resetQuantity('reinforcements_right', 'reinforcements', 1)">
                                <option value="">Select Reinforcements Right</option>
                                <option v-for="option in reinforcementsProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityReinforcementsRight">{{
                                exceedsQuantityReinforcementsRight
                            }}</small>
                        </div>
                    </div>
                    <div class="col-md-12 row mb-3 d-flex align-items-center">
                        <div class="col-md-4">
                            <label for="Addition3">Addition 3:</label>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.reinforcements.reinforcements_left_id_3"
                                @change="resetQuantity('reinforcements_left', 'reinforcements', 1)">
                                <option value="">Select Reinforcements Left</option>
                                <option v-for="option in reinforcementsProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityReinforcementsLeft">{{
                                exceedsQuantityReinforcementsLeft
                            }}</small>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.reinforcements.reinforcements_right_id_3"
                                @change="resetQuantity('reinforcements_right', 'reinforcements', 1)">
                                <option value="">Select Reinforcements Right</option>
                                <option v-for="option in reinforcementsProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityReinforcementsRight">{{
                                exceedsQuantityReinforcementsRight
                            }}</small>
                        </div>
                    </div>
                    <div class="col-md-12 row mb-3 d-flex align-items-center">
                        <div class="col-md-4">
                            <label for="Addition4">Addition 4:</label>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.reinforcements.reinforcements_left_id_4"
                                @change="resetQuantity('reinforcements_left', 'reinforcements', 1)">
                                <option value="">Select Reinforcements Left</option>
                                <option v-for="option in reinforcementsProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityReinforcementsLeft">{{
                                exceedsQuantityReinforcementsLeft
                            }}</small>
                        </div>
                        <div class="col-md-4">
                            <select class="form-control col-md-12" v-model="formData.reinforcements.reinforcements_right_id_4"
                                @change="resetQuantity('reinforcements_right', 'reinforcements', 1)">
                                <option value="">Select Reinforcements Right</option>
                                <option v-for="option in reinforcementsProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityReinforcementsRight">{{
                                exceedsQuantityReinforcementsRight
                            }}</small>
                        </div>
                    </div>
                    <div class="col-md-12 row mb-3 d-flex align-items-center">
                        <div class="col-md-4 mb-3">
                            <label for="Addition5">Addition 5:</label>
                        </div>
                        <div class="col-md-4 mb-3">
                            <select class="form-control col-md-12" v-model="formData.reinforcements.reinforcements_left_id_5"
                                @change="resetQuantity('reinforcements_left', 'reinforcements', 1)">
                                <option value="">Select Reinforcements Left</option>
                                <option v-for="option in reinforcementsProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityReinforcementsLeft">{{
                                exceedsQuantityReinforcementsLeft
                            }}</small>
                        </div>
                        <div class="col-md-4 mb-3">
                            <select class="form-control col-md-12" v-model="formData.reinforcements.reinforcements_right_id_5"
                                @change="resetQuantity('reinforcements_right', 'reinforcements', 1)">
                                <option value="">Select Reinforcements Right</option>
                                <option v-for="option in reinforcementsProducts" :key="option.id" :value="option.id">
                                    {{ option.value }}
                                </option>
                            </select>
                            <small class="text-danger" v-if="exceedsQuantityReinforcementsRight">{{
                                exceedsQuantityReinforcementsRight
                            }}</small>
                        </div>
                        <div class="col-md-4">
                        </div>
                        <div class="col-md-4">
                        </div>
                        <div class="col-md-4">
                            <label for="sameAsLeft" class="mr-1">Same as Left:</label>
                            <input type="checkbox" v-model="sameAsLeft.reinforcements" @change="setRightSameAsLeft('reinforcements', 1)">
                        </div>
                    </div>
                </div>

                <!-- Notes -->
                <div class="row">
                    <div class="col-md-12 mb-3">
                        <label for="Notes">Notes</label>
                        <textarea class="form-control" name="Notes" v-model="formData.notes_left" id="Notes" rows="3"
                            placeholder="Enter Notes..."></textarea>
                    </div>
                    <div class="col-md-12 mb-3">
                        <label for="NotesFromWorkshop">Notes from Workshop</label>
                        <textarea class="form-control" v-model="formData.notes_workshop" name="NotesFromWorkshop" id="NotesFromWorkshop" rows="3"
                            placeholder="Enter Notes from Workshop..."></textarea>
                    </div>
                </div>

                <!-- Upload finished-product images -->
                <div class="row mt-2">
                    <div class="col-md-12">
                        <label class="font-weight-bold d-block mb-2">Upload images (finished product) — up to {{ maxFinishedImages }}</label>
                        <div class="d-flex flex-wrap">
                            <div v-for="img in finishedImages" :key="img.id" class="mr-2 mb-2 text-center" style="width:110px;">
                                <a :href="img.url" target="_blank">
                                    <img :src="img.url" style="width:100px;height:100px;object-fit:cover;border:1px solid #ddd;border-radius:4px;" />
                                </a>
                                <button type="button" class="btn btn-sm btn-link text-danger p-0" @click="deleteFinishedImage(img.id)">Delete</button>
                            </div>
                            <div v-if="finishedImages.length < maxFinishedImages"
                                 class="mr-2 mb-2 d-flex align-items-center justify-content-center"
                                 style="width:100px;height:100px;border:1px dashed #bbb;border-radius:4px;cursor:pointer;"
                                 @click="$refs.finishedImageInput.click()">
                                <span class="text-muted">{{ uploadingFinishedImage ? '…' : '+ Add' }}</span>
                            </div>
                        </div>
                        <input ref="finishedImageInput" type="file" accept="image/*" class="d-none" @change="onSelectFinishedImage" />
                        <small class="text-muted d-block">Saved with the Order ID, patient name and upload date in the filename.</small>
                    </div>
                </div>
            </div>
        </form>
    </modal>
</template>
<script>
import FormHelperMixins from "../../../../../../common/Mixin/Global/FormHelperMixins";
import ModalMixin from "../../../../../../common/Mixin/Global/ModalMixin";
import { CUSTOM_INSOLES_CUSTOMIZATION, ORDER_ITEM_IMAGES } from "../../../../../Config/ApiUrl-CP";
import { SELECTABLE_SHOE_TYPE, SELECTABLE_TEMPLATE_LENGTH_OR_WIDTH, SELECTABLE_SHOE_SIZE, SELECTABLE_MOULD } from "../../../../../Config/ApiUrl-CPB";
import CoreLibrary from "../../../../../../core/helpers/CoreLibrary";
import { axiosGet, axiosPost, axiosPatch, axiosDelete, urlGenerator } from "../../../../../../common/Helper/AxiosHelper";
import store from "../../../../../../store/Index";

export default {
    name: 'CustomInsolesCreateEditModal',
    props: {
        branchOrWarehouseId: {
            type: Number, // Adjust the type according to your data type
            required: true,
        },
        orderProductId: {
            type: Number, // Adjust the type according to your data type
            required: false, // You can set this to true if it's required
        },
        orderId: {
            type: Number, // Adjust the type according to your data type
            required: false, // You can set this to true if it's required
        },
        item: {
            type: Object,
            required: true
        },
        invoice: {
            type: Object,
            required: true
        }
    },
    extends: CoreLibrary,
    mixins: [FormHelperMixins, ModalMixin],
    data() {
        return {
            CUSTOM_INSOLES_CUSTOMIZATION,
            finishedImages: [],
            maxFinishedImages: 4,
            uploadingFinishedImage: false,
            orderQrImage: '',
            CustomInsoles: [],
            shoeTypeOptions: [],
            shoeSizeOptions: [],
            mouldOptions: [],
            templateLengthOrWidthOptions: [],
            formData: {
                shoe_type: null,
                shoe_heel: null,
                shoe_brand: null,
                shoe_size: null,
                shoe_volume: '',
                made_for_shoes: 0,
                insole_width: '',
                notes_workshop: null,
                notes_left: null,
                linked_orders: 0,
                order_id: null,
                template_length: null,
                template_width: null,
                custom_modules: null,
                mould: null,
                medial_wall: '',
                back_cover: '',
                sidas_last_size: '',
                delivery: '',
                composite_id: '',
                layering:{
                    layering_left_id_1: '',
                    layering_left_id_2: '',
                    layering_left_id_3: '',
                    layering_left_id_4: '',
                    layering_left_id_5: '',
                    layering_right_id_1: '',
                    layering_right_id_2: '',
                    layering_right_id_3: '',
                    layering_right_id_4: '',
                    layering_right_id_5: '',
                },
                reinforcements:{
                    reinforcements_left_id_1: '',
                    reinforcements_left_id_2: '',
                    reinforcements_left_id_3: '',
                    reinforcements_left_id_4: '',
                    reinforcements_left_id_5: '',
                    reinforcements_right_id_1: '',
                    reinforcements_right_id_2: '',
                    reinforcements_right_id_3: '',
                    reinforcements_right_id_4: '',
                    reinforcements_right_id_5: '',
                },
            },
            compositeProducts: [],
            layeringProducts: [],
            reinforcementsProducts: [],
            techniqueOptions: [],
            medialWallOptions: [],
            backCoverOptions: [],
            sidasLastSizeOptions: [],
            insoleWidths: [
                { id: 'standard', value: this.$t('Standard') },
                { id: 'wide', value: this.$t('Wide') },
                { id: 'cup', value: this.$t('Cup') },
            ],
            shoeVolumes: [
                { id: 'low', value: this.$t('Low') },
                { id: 'mid', value: this.$t('Mid') },
                { id: 'high', value: this.$t('High') },
            ],
            yesNoOptions: [
                { id: 1, value: this.$t('Yes') },
                { id: 0, value: this.$t('No') },
            ],
            linkedOrders: [
                {
                    id: 1,
                    value: this.$t('Yes')
                },
                {
                    id: 0,
                    value: this.$t('No')
                }
            ],
            dropdownOptions: [
                { label: "Demo", value: "Demo" },
                { label: "Demo 1", value: "Demo 1" },
                { label: "Demo 2", value: "Demo 2" },
                { label: "Demo 3", value: "Demo 3" },
                { label: "Demo 4", value: "Demo 4" },
                { label: "Demo 5", value: "Demo 5" },
            ],
            // shoeTypeOptions: {
            //     url: urlGenerator(SELECTABLE_SHOE_TYPE),
            //     query_name: "search_by_name",
            //     per_page: 50,
            //     loader: "app-pre-loader", // by default 'app-overlay-loader'
            //     modifire: (value) => ({ id: value.id, value: value.item_label }),
            //     // modifire: ({ id, item_label: value }) => ({ id, value }),
            // },
            // templateLengthOrWidthOptions: {
            //     url: urlGenerator(SELECTABLE_TEMPLATE_LENGTH_OR_WIDTH),
            //     query_name: "search_by_name",
            //     per_page: 50,
            //     loader: "app-pre-loader", // by default 'app-overlay-loader'
            //     modifire: (value) => ({ id: value.id, value: value.item_label }),
            //     // modifire: ({ id, item_label: value }) => ({ id, value }),
            // },
            // shoeSizeOptions: {
            //     url: urlGenerator(SELECTABLE_SHOE_SIZE),
            //     query_name: "search_by_name",
            //     per_page: 50,
            //     loader: "app-pre-loader", // by default 'app-overlay-loader'
            //     modifire: (value) => ({ id: value.id, value: value.item_label }),
            //     // modifire: ({ id, item_label: value }) => ({ id, value }),
            // },
            // mouldOptions: {
            //     url: urlGenerator(SELECTABLE_MOULD),
            //     query_name: "search_by_name",
            //     per_page: 50,
            //     loader: "app-pre-loader", // by default 'app-overlay-loader'
            //     modifire: (value) => ({ id: value.id, value: value.item_label }),
            //     // modifire: ({ id, item_label: value }) => ({ id, value }),
            // },
            selectedType: "Demo",
            Mould: [
                { label: '3D scan', value: this.$t('3D scan') },
                { label: 'POP cast', value: this.$t('POP cast') },
            ],
            selectedMould: "3D scan",
            exceedsQuantityLayeringLeft: null,
            exceedsQuantityLayeringRight: null,
            exceedsQuantityReinforcementsLeft: null,
            exceedsQuantityReinforcementsRight: null,
            sameAsLeft: {
                layering: false,
                reinforcements:false
            },
        };
    },
    mounted(){
        // let self = this;
        // $('#shoeType').select2({ placeholder: 'Select Shoe Type',dropdownParent: $("#custom-insoles-modal") }).on("change", function () {
        //     self.onSelectChangeShoeType(this.value);
        // });
        // $('#shoeSize').select2({ placeholder: 'Select Shoe Size',dropdownParent: $("#custom-insoles-modal") }).on("change", function () {
        //     self.onSelectChangeShoeSize(this.value);
        // });
        // $('#templateWidth').select2({ placeholder: 'Select Template Width',dropdownParent: $("#custom-insoles-modal") }).on("change", function () {
        //     self.onSelectChangeTemplateWidth(this.value);
        // });
        // $('#templateLength').select2({ placeholder: 'Select Template Length',dropdownParent: $("#custom-insoles-modal") }).on("change", function () {
        //     self.onSelectChangeTemplateLength(this.value);
        // });
        // $('#mould').select2({ placeholder: 'Select Mould',dropdownParent: $("#custom-insoles-modal") }).on("change", function () {
        //     self.onSelectChangeMould(this.value);
        // });
    },
    created() {
        this.customInsolesOptions();
        this.fetchCustomInsoles();
        this.fetchShoeTypes();
        this.fetchShoeSizes();
        this.fetchTemplateLengthOrWidth();
        this.fetchMould();
        this.fetchTechnique();
        this.fetchMedialWall();
        this.fetchBackCover();
        this.fetchSidasLastSize();
        this.fetchFinishedImages();
        this.fetchOrderQr();
    },
    computed: {
        selectedBranchId() {
            return store.state.auth.branch_or_warehouse_id;
        },
        patientName() {
            const c = this.invoice && this.invoice.customer;
            return c ? (c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim()) : '-';
        },
        patientAge() {
            const c = this.invoice && this.invoice.customer;
            return (c && c.age) ? `${c.age} yrs` : '-';
        },
        patientWeight() {
            const c = this.invoice && this.invoice.customer;
            return (c && c.weight) ? c.weight : '-';
        },
        branchName() {
            return this.invoice && this.invoice.branch_or_warehouse ? this.invoice.branch_or_warehouse.name : '-';
        },
        practitionerName() {
            return this.invoice && this.invoice.created_by ? this.invoice.created_by.full_name : '-';
        },
        orderDate() {
            return this.formatDate(this.item && this.item.ordered_at ? this.item.ordered_at : (this.invoice && this.invoice.ordered_at)) || '-';
        },
        targetDateDisplay() {
            const d = this.item && this.item.order_product_delivery_details;
            return d && d.target_date ? this.formatDate(d.target_date) : '-';
        },
        revisedTargetDisplay() {
            const d = this.item && this.item.order_product_delivery_details;
            return d && d.revised_target_date ? this.formatDate(d.revised_target_date) : '-';
        },
        itemName() {
            return this.item && this.item.variant ? this.item.variant.name : '-';
        },
        qtyDisplay() {
            const qty = this.item && this.item.quantity != null ? this.item.quantity : '';
            const unit = this.item && this.item.variant && this.item.variant.product && this.item.variant.product.unit
                ? this.item.variant.product.unit.name : '';
            return unit ? `${qty} ${unit}` : `${qty}`;
        },
        workflowSteps() {
            const steps = (this.item && this.item.order_product_technicians) ? this.item.order_product_technicians.slice() : [];
            return steps.sort((a, b) => (a.id || 0) - (b.id || 0));
        },
        modeLabel() {
            return (this.item && this.item.mode === 'parallel') ? 'Parallel' : 'Sequential';
        },
        isCustomComposite() {
            const sel = this.compositeProducts.find(o => o.id === this.formData.composite_id);
            return !!(sel && String(sel.value).trim().toLowerCase() === 'custom');
        },
        isSidasTechnique() {
            const sel = this.techniqueOptions.find(o => o.id === this.formData.mould);
            const label = sel ? String(sel.value).trim().toLowerCase() : '';
            return label === 'sidas dome last' || label === 'sidas non-dome last';
        }
    },
    methods: {
        formatDate(value) {
            if (!value) return '';
            const d = new Date(value);
            if (isNaN(d.getTime())) {
                // fall back to first 10 chars (e.g. "2026-06-14")
                return String(value).substring(0, 10);
            }
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}-${mm}-${yyyy}`;
        },
        getStepName(s) {
            if (!s) return '';
            if (s.user_type == 0) {
                return s.technician ? s.technician.technician_name : '';
            }
            return s.technicianuser ? s.technicianuser.full_name : '';
        },
        onSelectChangeShoeType(val) {
            this.formData.shoe_type = val;
        },
        onSelectChangeShoeSize(val) {
            this.formData.shoe_size = val;
        },
        onSelectChangeTemplateLength(val) {
            this.formData.template_length = val;
        },
        onSelectChangeTemplateWidth(val) {
            this.formData.template_width = val;
        },
        onSelectChangeMould(val) {
            this.formData.mould = val;
        },
        fetchShoeTypes() {
            this.shoeTypeOptions = [];
            axiosGet(SELECTABLE_SHOE_TYPE).then(response => {
                for (const item of response.data.data) {
                    this.shoeTypeOptions.push({
                        id: item.id,
                        value: item.item_label,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        fetchShoeSizes() {
            this.shoeSizeOptions = [];
            axiosGet(SELECTABLE_SHOE_SIZE).then(response => {
                for (const item of response.data.data) {
                    this.shoeSizeOptions.push({
                        id: item.id,
                        value: item.item_label,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        fetchTemplateLengthOrWidth() {
            this.templateLengthOrWidthOptions = [];
            axiosGet(SELECTABLE_TEMPLATE_LENGTH_OR_WIDTH).then(response => {
                for (const item of response.data.data) {
                    this.templateLengthOrWidthOptions.push({
                        id: item.id,
                        value: item.item_label,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        fetchMould() {
            this.mouldOptions = [];
            axiosGet(SELECTABLE_MOULD).then(response => {
                for (const item of response.data.data) {
                    this.mouldOptions.push({
                        id: item.id,
                        value: item.item_label,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        fetchListByName(listName, target) {
            this[target] = [];
            axiosGet(`app/selectable-list?name=${encodeURIComponent(listName)}`).then(response => {
                // endpoint returns a plain array of {id, item_label}
                const rows = Array.isArray(response.data) ? response.data : (response.data.data || []);
                for (const item of rows) {
                    this[target].push({
                        id: item.id,
                        value: item.item_label,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        fetchTechnique() {
            this.fetchListByName('Insole Moulding', 'techniqueOptions');
        },
        fetchFinishedImages() {
            const opid = this.orderProductId || (this.item && this.item.id);
            if (!opid) return;
            axiosGet(`${ORDER_ITEM_IMAGES}?order_product_id=${opid}`).then(response => {
                this.finishedImages = response.data.images || [];
                if (response.data.max) this.maxFinishedImages = response.data.max;
            }).catch(error => {
                console.error(error);
            });
        },
        onSelectFinishedImage(event) {
            const file = event.target.files && event.target.files[0];
            event.target.value = '';
            if (!file) return;
            if (this.finishedImages.length >= this.maxFinishedImages) {
                this.$toastr.e(`You can upload up to ${this.maxFinishedImages} images.`);
                return;
            }
            const orderId = this.orderId || (this.invoice && this.invoice.id);
            const opid = this.orderProductId || (this.item && this.item.id);
            const data = new FormData();
            data.append('order_id', orderId);
            data.append('order_product_id', opid);
            data.append('image', file);
            this.uploadingFinishedImage = true;
            axiosPost(ORDER_ITEM_IMAGES, data).then(response => {
                this.finishedImages.push(response.data.image);
                this.$toastr.s('Image uploaded.');
            }).catch(({ response }) => {
                this.$toastr.e(response && response.data ? response.data.message : 'Upload failed.');
            }).finally(() => {
                this.uploadingFinishedImage = false;
            });
        },
        deleteFinishedImage(id) {
            axiosDelete(`${ORDER_ITEM_IMAGES}/${id}`).then(() => {
                this.finishedImages = this.finishedImages.filter(i => i.id !== id);
            }).catch(() => {
                this.$toastr.e('Could not delete image.');
            });
        },
        fetchMedialWall() {
            this.fetchListByName('Insole Flange', 'medialWallOptions');
        },
        fetchBackCover() {
            this.fetchListByName('Insole Back Cover', 'backCoverOptions');
        },
        fetchSidasLastSize() {
            this.fetchListByName('Sidas last size', 'sidasLastSizeOptions');
        },
        fetchOrderQr() {
            const orderId = this.orderId || (this.invoice && this.invoice.id);
            if (!orderId) return;
            axiosGet(`app/order/${orderId}/qr`).then(response => {
                this.orderQrImage = response.data.image;
            }).catch(error => {
                console.error(error);
            });
        },
        setRightSameAsLeft(fieldName, rowNumber) {
            if(typeof fieldName != 'undefined') {
                if (this.sameAsLeft[fieldName]) {
                    for (let i = rowNumber; i <= 5; i++) {
                        this.formData[fieldName][`${fieldName}_right_id_${i}`] = this.formData[fieldName][`${fieldName}_left_id_${i}`];
                    }
                } else {
                    for (let i = 1; i <= 5; i++) {
                        this.formData[fieldName][`${fieldName}_right_id_${i}`] = '';
                    }
                }
            }
        },
        resetQuantity(fieldName, field_name, rowNumber) {
            // Set the quantity to 0 for the specified field
            this.formData[fieldName + '_quantity'] = 1;
            this.handleQuantityInput(fieldName);
            this.setRightSameAsLeft(field_name, rowNumber);
        },
        handleQuantityInput(fieldName) {
            const enteredQuantity = this.formData[fieldName + '_quantity'];
            let selectedOption;

            if (fieldName === 'layering_left' || fieldName === 'layering_right') {
                selectedOption = this.layeringProducts.find(item => item.id === this.formData[fieldName + '_id']);
            } else {
                selectedOption = this.reinforcementsProducts.find(item => item.id === this.formData[fieldName + '_id']);
            }

            // Use the respective error message variable based on the field name
            const errorVariableName = 'exceedsQuantity' + fieldName.charAt(0).toUpperCase() + fieldName.slice(1);

            if (!selectedOption) {
                // The selected option is null, which means the product is not available
                this[errorVariableName] = `No product available for ${fieldName.replace('_', ' ')}.`;
            } else if (!this.validateQuantity(enteredQuantity, selectedOption.quantity)) {
                this[errorVariableName] = `The entered quantity (${enteredQuantity}) exceeds the available quantity (${selectedOption.quantity}) for ${fieldName.replace('_', ' ')}.`;
            } else {
                this[errorVariableName] = null; // Reset the error message if the quantity is valid
            }

            // Update the stockId, branchId, and variantId in formData
            if (selectedOption) {
                this.formData[fieldName + '_stock_id'] = selectedOption.stockId;
                this.formData[fieldName + '_branch_id'] = selectedOption.branchId;
                this.formData[fieldName + '_variant_id'] = selectedOption.variantId;
            }
        },
        validateQuantity(quantity, availableQty) {
            return quantity <= availableQty;
        },
        customInsolesOptions() {
            axiosGet(`app/selectable/custom-insoles-products?branch_or_warehouse_id=${this.branchOrWarehouseId}`).then(response => {
                const data = response.data.data;
                for (const items of data) {
                    const option = {
                        id: items.variant.id,
                        value: items.variant.name,
                        quantity: items.available_qty,
                        stockId: items.id,
                        branchId: items.branch_or_warehouse_id,
                        variantId: items.variant_id,
                    };
                    if (items.variant.product.sub_category_id === 5) {
                        // Product has subcategory 5 (compositeProducts)
                        this.compositeProducts.push(option);
                    } else if (items.variant.product.sub_category_id === 6) {
                        // Product has subcategory 6 (layeringProducts)
                        this.layeringProducts.push(option);
                    } else if (items.variant.product.sub_category_id === 7) {
                        // Product has subcategory 7 (reinforcementsProducts)
                        this.reinforcementsProducts.push(option);
                    }
                }
            }).catch(error => {
                console.error(error);
            });
        },
        async fetchCustomInsoles() {
            try {
                await axiosGet(`${CUSTOM_INSOLES_CUSTOMIZATION}/${this.orderProductId}`).then(response => {
                    let resp = response.data[0];
                    // this.formData = data;
                    if(typeof resp != 'undefined' && resp.id > 0) {
                        this.formData.id = resp.id;
                        this.formData.shoe_type = resp.shoe_type;
                        this.formData.shoe_size = resp.shoe_size;
                        this.formData.shoe_heel = resp.shoe_heel;
                        this.formData.shoe_brand = resp.shoe_brand;
                        this.formData.shoe_volume = resp.shoe_volume || '';
                        this.formData.made_for_shoes = resp.made_for_shoes ? 1 : 0;
                        this.formData.insole_width = resp.insole_width || '';
                        this.formData.notes_workshop = resp.notes_workshop;
                        this.formData.notes_left = resp.notes_left;
                        this.formData.linked_orders = resp.linked_orders;
                        this.formData.order_id = resp.order_id;
                        this.formData.medial_wall = resp.medial_walls;
                        this.formData.back_cover = resp.back_cover || '';
                        this.formData.sidas_last_size = resp.sidas_last_size || '';
                        this.formData.delivery = resp.delivery;
                        this.formData.composite_id = resp.compositeid;
                        this.formData.template_length = resp.template_length;
                        this.formData.template_width = resp.template_width;
                        this.formData.custom_modules = resp.custom_modules;
                        this.formData.mould = resp.moulds;
                        let jsonData = JSON.parse(resp.jsondata);
                        let dataKeysLayering = Object.keys(jsonData.layering);
                        let dataKeysReinforcements = Object.keys(jsonData.reinforcements);
                        if(dataKeysLayering.length > 0) {
                            for (const keys of dataKeysLayering) {
                                this.formData.layering[keys] = jsonData.layering[keys]??'';
                            }
                        }
                        if(dataKeysReinforcements.length > 0) {
                            for (const keys of dataKeysReinforcements) {
                                this.formData.reinforcements[keys] = jsonData.reinforcements[keys]??'';
                            }
                        }
                    }
                });
            } catch (error) {
                console.error(error);
            }
        },
        submitData() {
            // Sidas last size is mandatory for the Sidas moulding techniques.
            if (this.isSidasTechnique && !this.formData.sidas_last_size) {
                this.$toastr.e('Please select the Sidas last size.');
                return;
            }

            this.fieldStatus.isSubmit = true;
            this.loading = true;
            this.message = '';
            this.errors = {};

            // Include the selectedBranchId in the formData
            this.formData.branch_id = this.selectedBranchId;
            this.formData.order_id = this.orderId;
            this.formData.order_product_id = this.orderProductId;
            
            this.save(this.formData);
        },
        afterSuccess({ data }) {
            this.formData = {};
            this.preloader = false;
            $('#custom-insoles-modal').modal('hide');
            this.$emit('update-values');
            this.$emit('input', false);
            this.toastAndReload(data.message, 'custom-insoles-table');
        },
        afterSuccessFromGetEditData(response) {
            this.preloader = false;
            this.formData = response.data;
        },
    },
}
</script>

<style>
.required>label:first-child:after {
    color: #e32;
    content: ' *';
    display: inline;
}
/* Increase the width of the Select2 input */
.select2-container--default .select2-selection--single {
    width: 100%;
    border: 1px solid #ced4da;
}

/* Increase the height of the Select2 input */
.select2-container--default .select2-selection--single {
    height: 40px;
}

/* Increase padding and font size */
.select2-container--default .select2-selection--single .select2-selection__rendered {
    padding: 5px 10px;
    font-size: 16px;
}
</style>